/** Debounced autosave with optimistic concurrency. `flush()` saves immediately (before renders). */

import { api, ApiError } from "../api";
import { useEditor } from "./store";

const DELAY = 1200;
let timer: number | undefined;
let inflight: Promise<void> | null = null;

async function saveNow(): Promise<void> {
  const st = useEditor.getState();
  const p = st.project;
  if (!p || st.saveState === "conflict") return;
  if (st.saveState !== "dirty" && st.saveState !== "error") return;
  useEditor.setState({ saveState: "saving" });
  try {
    const r = await api.save({ ...p, revision: st.revision });
    const now = useEditor.getState();
    // edits made while saving keep the state dirty and schedule another save
    useEditor.setState({ revision: r.revision, saveState: now.project === p ? "saved" : "dirty", saveError: "" });
    if (now.project !== p) schedule();
  } catch (e) {
    if (e instanceof ApiError && e.status === 409) useEditor.setState({ saveState: "conflict", saveError: e.message });
    else {
      useEditor.setState({ saveState: "error", saveError: (e as Error).message });
      schedule(5000);
    }
  }
}

export function schedule(delay = DELAY): void {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => {
    if (useEditor.getState().gesture) return schedule(400); // wait for the drag to finish
    inflight = saveNow().finally(() => (inflight = null));
  }, delay);
}

export async function flush(): Promise<void> {
  window.clearTimeout(timer);
  if (inflight) await inflight;
  await saveNow();
  if (useEditor.getState().saveState !== "saved") throw new Error(useEditor.getState().saveError || "could not save");
}

// ------------------------------------------------------------------------------- local draft

/** Unsaved edits are mirrored to browser storage until the server has them (crash recovery). */
export const draftKey = (pid: string) => `studio:draft:${pid}`;

export interface Draft {
  savedAt: number;
  revision: number;
  project: unknown;
}

let draftTimer: number | undefined;

function writeDraft() {
  const st = useEditor.getState();
  if (!st.project) return;
  try {
    if (st.saveState === "saved") localStorage.removeItem(draftKey(st.project.id));
    else localStorage.setItem(draftKey(st.project.id), JSON.stringify({ savedAt: Date.now(), revision: st.revision, project: st.project } satisfies Draft));
  } catch {
    /* storage full or blocked: recovery is best-effort */
  }
}

export function readDraft(pid: string): Draft | null {
  try {
    const raw = localStorage.getItem(draftKey(pid));
    return raw ? (JSON.parse(raw) as Draft) : null;
  } catch {
    return null;
  }
}

export function dropDraft(pid: string) {
  try {
    localStorage.removeItem(draftKey(pid));
  } catch {
    /* ignore */
  }
}

/** Start autosaving the open project; returns an unsubscribe function. */
export function startAutosave(): () => void {
  const unsub = useEditor.subscribe((s, prev) => {
    if (s.project !== prev.project && s.saveState === "dirty") schedule();
    if (s.project !== prev.project || s.saveState !== prev.saveState) {
      window.clearTimeout(draftTimer);
      draftTimer = window.setTimeout(writeDraft, s.saveState === "saved" ? 0 : 500);
    }
  });
  const beforeUnload = (e: BeforeUnloadEvent) => {
    const st = useEditor.getState().saveState;
    if (st === "dirty" || st === "saving") {
      writeDraft();
      void saveNow();
      e.preventDefault();
    }
  };
  window.addEventListener("beforeunload", beforeUnload);
  return () => {
    unsub();
    window.removeEventListener("beforeunload", beforeUnload);
    window.clearTimeout(timer);
    window.clearTimeout(draftTimer);
  };
}
